import { useState, useEffect } from 'react'
import {
  User,
  Bell,
  Shield,
  Palette,
  Globe,
  Save,
  Moon,
  Sun,
  AlertCircle,
  CheckCircle
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import Navigation from '../components/Navigation'

export default function SettingsPage() {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState('profile')
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [notifications, setNotifications] = useState({
    email: true,
    push: false,
    strategy: true,
    trade: true
  })
  const [profileData, setProfileData] = useState({
    username: user?.username || '',
    email: user?.email || ''
  })

  // 当用户数据加载后更新 profileData
  useEffect(() => {
    if (user) {
      setProfileData({
        username: user.username || '',
        email: user.email || ''
      })
    }
  }, [user])

  const tabs = [
    { id: 'profile', name: '个人资料', icon: User },
    { id: 'notifications', name: '通知设置', icon: Bell },
    { id: 'security', name: '安全设置', icon: Shield },
    { id: 'appearance', name: '外观设置', icon: Palette },
    { id: 'language', name: '语言设置', icon: Globe }
  ]

  const handleSave = async () => {
    try {
      setLoading(true)
      setError(null)
      setSuccess(null)

      if (activeTab === 'profile') {
        // 更新用户资料
        const { error } = await supabase.auth.updateUser({
          data: { username: profileData.username }
        })

        if (error) throw error
        setSuccess('个人资料更新成功')
      } else if (activeTab === 'notifications') {
        // 保存通知设置到用户配置文件
        const { error } = await supabase
          .from('user_profiles')
          .upsert({
            user_id: user?.id,
            notification_settings: notifications
          })

        if (error) throw error
        setSuccess('通知设置保存成功')
      }

      // 3秒后清除成功消息
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Error saving settings:', err)
      setError(err instanceof Error ? err.message : '保存设置失败')
    } finally {
      setLoading(false)
    }
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-white mb-4">个人资料</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">用户名</label>
                  <input
                    type="text"
                    value={profileData.username}
                    onChange={(e) => setProfileData(prev => ({ ...prev, username: e.target.value }))}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">邮箱</label>
                  <input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>
              </div>
            </div>
          </div>
        )
      
      case 'notifications':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-white mb-4">通知设置</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-white font-medium">邮件通知</h4>
                    <p className="text-white/60 text-sm">接收重要通知的邮件</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notifications.email}
                    onChange={(e) => setNotifications(prev => ({ ...prev, email: e.target.checked }))}
                    className="w-4 h-4 text-violet-600 bg-white/10 border-white/20 rounded focus:ring-violet-500"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-white font-medium">推送通知</h4>
                    <p className="text-white/60 text-sm">接收实时推送通知</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notifications.push}
                    onChange={(e) => setNotifications(prev => ({ ...prev, push: e.target.checked }))}
                    className="w-4 h-4 text-violet-600 bg-white/10 border-white/20 rounded focus:ring-violet-500"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-white font-medium">策略通知</h4>
                    <p className="text-white/60 text-sm">策略状态变化通知</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notifications.strategy}
                    onChange={(e) => setNotifications(prev => ({ ...prev, strategy: e.target.checked }))}
                    className="w-4 h-4 text-violet-600 bg-white/10 border-white/20 rounded focus:ring-violet-500"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-white font-medium">交易通知</h4>
                    <p className="text-white/60 text-sm">交易执行和结果通知</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notifications.trade}
                    onChange={(e) => setNotifications(prev => ({ ...prev, trade: e.target.checked }))}
                    className="w-4 h-4 text-violet-600 bg-white/10 border-white/20 rounded focus:ring-violet-500"
                  />
                </div>
              </div>
            </div>
          </div>
        )
      
      case 'security':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-white mb-4">安全设置</h3>
              <div className="space-y-4">
                <button className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-colors text-left">
                  <h4 className="font-medium">修改密码</h4>
                  <p className="text-white/60 text-sm">更新您的账户密码</p>
                </button>
                <button className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-colors text-left">
                  <h4 className="font-medium">两步验证</h4>
                  <p className="text-white/60 text-sm">启用两步验证以提高安全性</p>
                </button>
                <button className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-colors text-left">
                  <h4 className="font-medium">登录历史</h4>
                  <p className="text-white/60 text-sm">查看账户登录记录</p>
                </button>
              </div>
            </div>
          </div>
        )
      
      case 'appearance':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-white mb-4">外观设置</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-white font-medium">深色模式</h4>
                    <p className="text-white/60 text-sm">切换深色/浅色主题</p>
                  </div>
                  <button
                    onClick={() => setIsDarkMode(!isDarkMode)}
                    className="flex items-center px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-colors"
                  >
                    {isDarkMode ? <Moon className="w-4 h-4 mr-2" /> : <Sun className="w-4 h-4 mr-2" />}
                    {isDarkMode ? '深色' : '浅色'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      
      case 'language':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-white mb-4">语言设置</h3>
              <div className="space-y-4">
                <select className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500">
                  <option value="zh">中文 (简体)</option>
                  <option value="en">English</option>
                </select>
              </div>
            </div>
          </div>
        )
      
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen animate-fadein">
      <Navigation />
      {/* 主要内容 */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
        {/* 页面头部 */}
        <div className="card mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gradient mb-2">设置</h1>
            <p className="text-sm sm:text-base text-white/70">管理您的账户设置和偏好</p>
          </div>
          <button 
            onClick={handleSave} 
            disabled={loading}
            className="btn-primary flex items-center justify-center sm:justify-start"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? '保存中...' : '保存设置'}
          </button>
        </div>
      </div>

      {/* 错误和成功消息 */}
      {error && (
        <div className="card mb-4 sm:mb-6">
          <div className="flex items-center p-4 bg-red-500/20 border border-red-500/30 rounded-xl">
            <AlertCircle className="w-5 h-5 text-red-400 mr-3" />
            <p className="text-red-400">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="card mb-4 sm:mb-6">
          <div className="flex items-center p-4 bg-green-500/20 border border-green-500/30 rounded-xl">
            <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
            <p className="text-green-400">{success}</p>
          </div>
        </div>
      )}

      {/* 设置内容 */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        {/* 侧边栏 */}
        <div className="lg:col-span-1">
          <div className="card">
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                      activeTab === tab.id
                        ? 'bg-violet-500/20 text-white border border-violet-500/30'
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-3" />
                    {tab.name}
                  </button>
                )
              })}
            </nav>
          </div>
        </div>

        {/* 主内容 */}
        <div className="lg:col-span-3">
          <div className="card">
            {renderTabContent()}
          </div>
        </div>
      </div>
      </main>
    </div>
  )
} 